import { getRuntimeConfig } from "@/app/config/runtime-config";
import { rejectIfDomainLegacyOwned } from "@/core/write-authority/domain-legacy-owned";
import type { ApiBackedModuleId, RuntimeConfig } from "@/types/clm-web";

export interface ApiRequestOptions extends Omit<RequestInit, "body"> {
  body?: BodyInit | Record<string, unknown> | unknown[];
  accessToken?: string;
  runtimeConfig?: RuntimeConfig;
  /** Accept a 202 that carries no body as `data: undefined`; every other response must be JSON. */
  allowEmptyBody?: boolean;
}

export interface ApiClientResponse<T> {
  data: T;
  response: Response;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    readonly response: Response,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export function getApiBaseUrl(
  moduleId: ApiBackedModuleId,
  runtimeConfig: RuntimeConfig = getRuntimeConfig(),
): string {
  const baseUrl = runtimeConfig.apiBaseUrls[moduleId];

  if (!baseUrl) {
    throw new Error(`Runtime config is missing apiBaseUrls.${moduleId}.`);
  }

  return baseUrl.replace(/\/$/, "");
}

export function buildApiUrl(
  moduleId: ApiBackedModuleId,
  path: `/${string}`,
  runtimeConfig?: RuntimeConfig,
): string {
  const baseUrl = getApiBaseUrl(moduleId, runtimeConfig);
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}

export async function apiRequest<T>(
  moduleId: ApiBackedModuleId,
  path: `/${string}`,
  options: ApiRequestOptions = {},
): Promise<ApiClientResponse<T>> {
  const { accessToken, runtimeConfig, allowEmptyBody, headers, body, ...requestOptions } = options;
  const requestHeaders = new Headers(headers);

  if (accessToken) {
    requestHeaders.set("Authorization", `Bearer ${accessToken}`);
  }

  let requestBody: BodyInit | undefined;
  if (body !== undefined) {
    if (isJsonLikeBody(body)) {
      requestBody = JSON.stringify(body);
      requestHeaders.set("Content-Type", requestHeaders.get("Content-Type") ?? "application/json");
    } else {
      requestBody = body;
    }
  }

  const response = await fetch(buildApiUrl(moduleId, path, runtimeConfig), {
    credentials: "include",
    ...requestOptions,
    headers: requestHeaders,
    body: requestBody,
  });

  if (!response.ok) {
    if (response.status === 409) {
      // 018 T036: a legacy-owned domain write is a typed rejection, not a
      // generic conflict. The body is read from a clone so an ordinary 409
      // still reaches the caller with its body unconsumed.
      const body: unknown = await response
        .clone()
        .json()
        .catch(() => undefined);
      const legacyOwned = rejectIfDomainLegacyOwned(response.status, body);
      if (legacyOwned) {
        throw legacyOwned;
      }
    }
    throw new ApiClientError(`API request failed with status ${response.status}.`, response);
  }

  // A 204 never has a body; a 202 may only where the caller opted in (courses retry-sync).
  const data = (
    response.status === 204
      ? undefined
      : allowEmptyBody && response.status === 202
        ? await readOptionalJson(response)
        : await response.json()
  ) as T;
  return { data, response };
}

async function readOptionalJson(response: Response): Promise<unknown> {
  const text = await response.text();
  return text === "" ? undefined : (JSON.parse(text) as unknown);
}

function isJsonLikeBody(
  body: ApiRequestOptions["body"],
): body is Record<string, unknown> | unknown[] {
  return (
    typeof body === "object" &&
    body !== null &&
    !(body instanceof FormData) &&
    !(body instanceof Blob) &&
    !(body instanceof URLSearchParams)
  );
}
